import React, { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getCountries, type Country } from '../../api/publicApi';
import { useTenantBranding } from '../../hooks/useTenantBranding';
import { cn } from '../../lib/cn';

// Hand-rolled inputs (Tailwind + tokens) instead of BaseUI: Styletron drops
// parts of BaseUI's base styles in deployed builds (SAM-167/169/173 class) —
// the overlay's search Input collapsed to a 6px black line on DEV (SAM-180).
const inputClass =
  'w-full rounded-8 border border-solid border-line bg-surface px-sm py-[10px] ' +
  'text-sm text-ink-900 outline-none placeholder:text-ink-400 focus:border-tenant focus:bg-white';

// Same country selector UX as the public events flow (PublicEventDetail):
// flag + dial-code trigger opening a searchable overlay, data from /country.
// Value contract: full "{dial} {number}" string, e.g. "+1 809 555 0000".

const DEFAULT_FLAG = 'https://raw.githubusercontent.com/lipis/flag-icon-css/master/flags/4x3/do.svg';
const DEFAULT_DIAL = '+1';

// Split a stored phone ("+52 55 1234 5678") into dial code + local number.
// Longest dial prefix wins; "+1" prefers DO (our default market).
const parsePhone = (
  value: string,
  countries: Country[],
): { dial: string; number: string; country?: Country } => {
  const trimmed = (value || '').trim();
  const matches = countries
    .filter((c) => c.phone_code && (trimmed === c.phone_code || trimmed.startsWith(c.phone_code + ' ')
      // E.164 as stored, no spaces: "+18095551072"
      || (trimmed.startsWith(c.phone_code) && /^\d+$/.test(trimmed.slice(c.phone_code.length)))))
    .sort(
      (a, b) =>
        b.phone_code.length - a.phone_code.length ||
        (a.country_code === 'DO' ? -1 : 0) - (b.country_code === 'DO' ? -1 : 0),
    );
  const match = matches[0];
  if (match) {
    return { dial: match.phone_code, number: trimmed.slice(match.phone_code.length).trim(), country: match };
  }
  return { dial: DEFAULT_DIAL, number: trimmed };
};

interface PhoneInputProps {
  /** Full stored value, e.g. "+1 809 555 0000" */
  value: string;
  /** Called with the full "{dial} {number}" string (or "" when the number is empty) */
  onChange: (value: string) => void;
}

export const PhoneInput: React.FC<PhoneInputProps> = ({ value, onChange }) => {
  const { tenantApiKey, tenantLoading } = useTenantBranding();
  const [pickerOpen, setPickerOpen] = useState(false);
  const [search, setSearch] = useState('');
  // Keeps a country chosen while the number field is still empty (the stored
  // value only carries the dial once there is a number to attach it to).
  const [pendingCountry, setPendingCountry] = useState<Country | null>(null);

  const { data: countries = [] } = useQuery({
    queryKey: ['countries', tenantApiKey],
    queryFn: () => getCountries(tenantApiKey || undefined),
    enabled: !tenantLoading,
    staleTime: Infinity,
  });

  const parsed = useMemo(() => parsePhone(value, countries), [value, countries]);
  const number = parsed.number;
  const dial = parsed.number || !pendingCountry ? parsed.dial : pendingCountry.phone_code;
  const country = parsed.number || !pendingCountry ? parsed.country : pendingCountry;
  const flag = country?.country_flag || DEFAULT_FLAG;

  const emit = (dialCode: string, num: string) => {
    const n = num.trim();
    onChange(n ? `${dialCode} ${n}` : '');
  };

  const filtered = countries.filter(
    (c) =>
      !search ||
      (c.country || '').toLowerCase().includes(search.toLowerCase()) ||
      (c.phone_code || '').includes(search),
  );

  return (
    <div style={{ display: 'flex', gap: 8 }}>
      <button
        type="button"
        aria-label="Country code"
        onClick={() => setPickerOpen(true)}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          flexShrink: 0,
          border: '2px solid #EEEEEE',
          borderRadius: 8,
          background: '#EEEEEE',
          fontSize: 14,
          padding: '0 10px',
          cursor: 'pointer',
        }}
      >
        <img src={flag} alt="" style={{ width: 20, height: 15, borderRadius: 2 }} />
        {dial} ▾
      </button>
      <div style={{ flex: 1 }}>
        <input
          className={inputClass}
          value={number}
          onChange={(e) => emit(dial, e.target.value.replace(/[^\d\s\-()]/g, ''))}
          placeholder="809 555 0000"
        />
      </div>

      {pickerOpen && (
        <div
          onClick={() => {
            setPickerOpen(false);
            setSearch('');
          }}
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0,0,0,0.3)',
            zIndex: 20000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 20,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              backgroundColor: '#fff',
              borderRadius: 12,
              width: '100%',
              maxWidth: 400,
              maxHeight: '70vh',
              padding: 16,
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <input
              autoFocus
              className={cn(inputClass, 'mb-xs')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search country…"
            />
            <div style={{ flex: 1, overflowY: 'auto' }}>
              {filtered.map((c) => (
                <div
                  key={c.country_code}
                  onClick={() => {
                    setPendingCountry(c);
                    emit(c.phone_code, number);
                    setPickerOpen(false);
                    setSearch('');
                  }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    padding: '10px 8px',
                    cursor: 'pointer',
                    borderRadius: 6,
                    fontSize: 14,
                  }}
                >
                  <img src={c.country_flag} alt="" style={{ width: 24, height: 18, borderRadius: 2 }} />
                  <span style={{ flex: 1 }}>{c.country}</span>
                  <span style={{ color: '#6B7280' }}>{c.phone_code}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
